import React from 'react';
import { Box, Chip, ChipProps, Typography } from '@mui/material';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Role, User } from '../../types';
import type { Language } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/date';
import UserActionsMenu from './UserActionsMenu';
import IdentityAvatar from '../common/IdentityAvatar';
import CompanyNameCell from '../common/CompanyNameCell';
import { getPersonInitials } from '../../utils/initials';

interface UserColumnOptions {
  t: (key: string) => string;
  language: Language;
  canWrite: boolean;
  getRoleColor: (role: Role) => ChipProps['color'];
  canEditUser: (user: User) => boolean;
  currentUserId?: string;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

export function createUserColumns(options: UserColumnOptions): ColumnDef<User>[] {
  const { t, language, canWrite, getRoleColor, canEditUser, currentUserId, onEdit, onDelete } = options;
  return [
    {
      id: 'fullName', accessorFn: user => `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username, header: t('common.name'),
      cell: ({ row }: CellContext<User, unknown>) => {
        const user = row.original;
        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
        return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 190 }}>
          <IdentityAvatar>
            {getPersonInitials(user.firstName, user.lastName, language, user.username) || '?'}
          </IdentityAvatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={700} sx={{ lineHeight: 1.3 }}>{name}</Typography>
            <Typography variant="caption" color="text.secondary">@{user.username}</Typography>
          </Box>
        </Box>;
      },
    },
    {
      accessorKey: 'email', header: t('common.email'),
      cell: ({ row }: CellContext<User, unknown>) => <Typography variant="body2" color={row.original.email ? 'text.primary' : 'text.secondary'} sx={{ overflowWrap: 'anywhere' }}>
        {row.original.email || t('users.noEmail')}
      </Typography>,
    },
    {
      accessorKey: 'role', header: t('users.role'),
      cell: ({ row }: CellContext<User, unknown>) => <Chip label={t(`role.${row.original.role}`)} color={getRoleColor(row.original.role)} size="small" sx={{ fontWeight: 600 }} />,
    },
    {
      id: 'company', header: t('users.company'), enableSorting: false,
      cell: ({ row }: CellContext<User, unknown>) => <CompanyNameCell name={row.original.companyName || row.original.company?.name} />,
    },
    {
      accessorKey: 'createdAt', header: t('users.created'),
      cell: ({ row }: CellContext<User, unknown>) => <Typography variant="body2" color="text.secondary">{row.original.createdAt ? formatDate(row.original.createdAt, language) : '—'}</Typography>,
    },
    ...(canWrite ? [{
      id: 'actions', header: t('common.actions'), enableSorting: false,
      cell: ({ row }: CellContext<User, unknown>) => canEditUser(row.original)
        ? <UserActionsMenu user={row.original} canDelete={row.original.id !== currentUserId} onEdit={onEdit} onDelete={onDelete} />
        : null,
    }] : []),
  ];
}
